import * as React from "react";
import { useState, useCallback, useRef } from "react";

interface TimeInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: string;
}

const TimeInput: React.FC<TimeInputProps> = ({
  value: controlledValue,
  onChange,
  onComplete,
  placeholder = "00.00",
  disabled = false,
  className = "",
  error,
}) => {
  const isControlled = controlledValue !== undefined;

  const [rawInternal, setRawInternal] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const raw = isControlled
    ? (controlledValue ?? "").replace(/\D/g, "").slice(0, 4)
    : rawInternal;

  const displayValue =
    raw.length <= 2 ? raw : raw.slice(0, 2) + "." + raw.slice(2);

  const updateRaw = useCallback(
    (nextRaw: string) => {
      if (!isControlled) setRawInternal(nextRaw);
      const display =
        nextRaw.length <= 2
          ? nextRaw
          : nextRaw.slice(0, 2) + "." + nextRaw.slice(2);
      onChange?.(display);
      if (nextRaw.length === 4) {
        onComplete?.(display);
      }
    },
    [isControlled, onChange, onComplete]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        e.preventDefault();
        updateRaw(raw.slice(0, -1));
        return;
      }
      if (e.key === "Delete") {
        e.preventDefault();
        updateRaw("");
        return;
      }
    },
    [raw, updateRaw]
  );

  const handleInput = useCallback(
    (e: any) => {
      const typed = e.target.value.replace(/\D/g, "");
      const incoming = typed.slice(raw.length);
      const next = (raw + incoming).slice(0, 4);
      updateRaw(next);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          const len = el.value.length;
          el.setSelectionRange(len, len);
        }
      });
    },
    [raw, updateRaw]
  );

  const isComplete = raw.length === 4;

  const wrapperClasses = [
    "relative inline-flex items-center justify-center",
    "transition-colors",
    focused
      ? "border-primary bg-white"
      : isComplete
      ? "border-green-500 bg-white"
      : "border-gray-300 bg-athensGray",
    disabled ? "opacity-50 cursor-not-allowed" : "cursor-text",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-col items-center">
      <div className={wrapperClasses} onClick={() => inputRef.current?.focus()}>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        onChange={() => {}}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={5}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        aria-label="Time entry in seconds and hundredths format"
        className={[
          "bg-transparent text-center text-xl font-bold text-secondary",
          "outline-none px-2 py-3 w-40",
          displayValue.length === 0 ? "text-opacity-30" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      />
      </div>
      {error && (
        <p className="text-white text-label mt-4 text-center">{error}</p>
      )}
    </div>
  );
};

export default TimeInput;
